import { SelectPanel, Spinner } from '@cycle-app/ui';
import { FC, useEffect } from 'react';
import { useController, Control } from 'react-hook-form';

import DropdownLayer from 'src/components/DropdownLayer/DropdownLayer';
import { useGithubRepositories } from 'src/hooks/api/queries/integrations/useGithubRepositories';
import useOptimizedBooleanState from 'src/hooks/useOptimizedBooleanState';
import { useLastRepositoryIdUsed, setLastRepositoryIdUsed } from 'src/reactives/githubRepository.reactive';
import { CreateGithubIssueFormValues } from 'src/types/integrations.types';
import { Layer } from 'src/types/layers.types';

import { Container, Label, Caret, Select } from './RepositoriesField.styles';

type Option = {
  label: string;
  value: string;
};

type Props = {
  control: Control<CreateGithubIssueFormValues>;
};

export const RepositoriesField: FC<Props> = ({ control }) => {
  const { repositories } = useGithubRepositories();
  const lastUsedRepositoryId = useLastRepositoryIdUsed();
  const [isRepositoriesVisible, { toggleCallback: toggleIsRepositoriesVisible }] = useOptimizedBooleanState(false);
  const {
    field: {
      onChange, onBlur, name, ref, value,
    },
  } = useController({
    name: 'repository',
    control,
    rules: { required: true },
    defaultValue: undefined,
  });

  useEffect(() => {
    if (value || !repositories.length) return;

    onChange(repositories.find(r => r.id === lastUsedRepositoryId) || repositories[0]);
  }, [value, repositories, onChange, lastUsedRepositoryId]);

  const options: Option[] = repositories.map(r => ({
    label: r.name,
    value: r.id,
  })) || [];

  return (
    <Container>
      <Label>Repository</Label>
      <DropdownLayer
        layer={Layer.DropdownModal}
        visible={isRepositoriesVisible}
        hide={() => {
          onBlur();
          toggleIsRepositoriesVisible();
        }}
        placement="bottom-start"
        content={(
          <SelectPanel
            selectedValue={value?.id}
            options={options}
            onOptionChange={onRepositorySelected}
          />
        )}
      >
        <Select
          name={name}
          ref={ref}
          onClick={toggleIsRepositoriesVisible}
          type="button"
          forceFocus={isRepositoriesVisible}
          disabled={!repositories.length}
        >
          {value ? value.name : <Spinner />}
          <Caret />
        </Select>
      </DropdownLayer>
    </Container>
  );

  function onRepositorySelected(option: Option) {
    const repository = repositories.find(r => r.id === option.value);

    if (!repository) return;

    onChange(repository);
    setLastRepositoryIdUsed(repository.id);
    toggleIsRepositoriesVisible();
  }
};
